import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Label } from '@/shared/components/ui/Label';
import type { SignalSuggestionItem } from '../types/tasks.types';

interface SignalSuggestionsSelectorProps {
  items: SignalSuggestionItem[];
  onToggle: (id: string, approved: boolean) => void;
  isLoading?: boolean;
}

export const SignalSuggestionsSelector: React.FC<
  SignalSuggestionsSelectorProps
> = ({ items, onToggle, isLoading = false }) => {
  const { t } = useTranslation('goals');

  if (!isLoading && items.length === 0) return null;

  return (
    <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 space-y-3">
      <div>
        <h4 className="text-xs font-medium text-blue-900">
          {t('relevantSignals')}
        </h4>
        <p className="text-xs text-muted-foreground mt-1">
          {isLoading
            ? t('loadingSignalSuggestions')
            : t('suggestedSignalsDescription')}
        </p>
      </div>
      {isLoading ? (
        <div
          className="flex items-center gap-2 py-2 text-xs text-muted-foreground"
          role="status"
          aria-label={t('loadingSignalSuggestions')}
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" />
          <span>{t('loadingSignalSuggestions')}</span>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start gap-3 text-xs rounded p-2 hover:bg-blue-50/50"
            >
              <Checkbox
                id={`signal-${item.id}`}
                checked={item.approved}
                onCheckedChange={(checked) =>
                  onToggle(item.id, checked === true)
                }
                className="mt-0.5 shrink-0"
                aria-label={t('includeSignalWithTask')}
              />
              <Label
                htmlFor={`signal-${item.id}`}
                className="text-gray-700 leading-relaxed cursor-pointer flex-1 font-normal"
              >
                {item.contextualTitle || item.signal}
              </Label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
